import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useSavePreisToolLegendThresholds } from '@/api/preis-tool/preis-tool.hooks';
import type { PreisToolLegendThresholds } from '@/api/preis-tool/preis-tool.types';
import { DsAlert, DsButton, DsInput, DsModal } from '@/modules/internal-v2/ds';
import { fmtDateTime, fmtLimitPct } from '../helpers';
import { DEFAULT_LEGEND_LIMITS, type LegendLimits } from '../row-states';

/** Accepted range in percent; the backend checks the same as shares. */
const DEAL_RANGE = { min: 1, max: 90 };
const LOW_MARGIN_RANGE = { min: 0.1, max: 50 };

interface LegendThresholdsDialogProps {
  /** `undefined` while loading: the defaults are shown. */
  thresholds: PreisToolLegendThresholds | undefined;
  /** `PREIS_TOOL_MANAGE`; without it the dialog only shows the limits. */
  canEdit: boolean;
  onClose: () => void;
}

/** 0.125 → "12,5" for the input field. */
const toPercentInput = (share: number) =>
  (share * 100).toLocaleString('de-DE', { maximumFractionDigits: 2, useGrouping: false });

/** "12,5", "12.5 %" → 12.5; `null` for anything else or more than two decimals. */
function parsePercentInput(input: string): number | null {
  const cleaned = input.trim().replace(/\s|%/g, '');
  if (!/^\d+([.,]\d{1,2})?$/.test(cleaned)) return null;
  return Number(cleaned.replace(',', '.'));
}

const inRange = (value: number | null, range: { min: number; max: number }): value is number =>
  value != null && value >= range.min && value <= range.max;

/**
 * Limits of the colour legend (EOS-61): from when an item is a "Deal" and
 * a sale price has a "low margin". Shared by every user; saved as shares.
 */
export function LegendThresholdsDialog({ thresholds, canEdit, onClose }: LegendThresholdsDialogProps) {
  const save = useSavePreisToolLegendThresholds();
  const { enqueueSnackbar } = useSnackbar();
  const current: LegendLimits = thresholds ?? DEFAULT_LEGEND_LIMITS;
  const [deal, setDeal] = useState(toPercentInput(current.dealMinSavingsPct));
  const [lowMargin, setLowMargin] = useState(toPercentInput(current.lowMarginPct));
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (!canEdit || save.isPending) return;
    const dealPct = parsePercentInput(deal);
    const lowMarginPct = parsePercentInput(lowMargin);
    if (!inRange(dealPct, DEAL_RANGE)) {
      return setError('Deal: bitte einen Wert von 1 bis 90 % mit höchstens zwei Nachkommastellen angeben.');
    }
    if (!inRange(lowMarginPct, LOW_MARGIN_RANGE)) {
      return setError('Niedrige Marge: bitte einen Wert von 0,1 bis 50 % mit höchstens zwei Nachkommastellen angeben.');
    }
    setError(null);
    save.mutate(
      {
        dealMinSavingsPct: Number((dealPct / 100).toFixed(4)),
        lowMarginPct: Number((lowMarginPct / 100).toFixed(4)),
      },
      {
        onSuccess: (saved) => {
          enqueueSnackbar(
            `Grenzen gespeichert: Deal ab ${fmtLimitPct(saved.dealMinSavingsPct)}, niedrige Marge unter ${fmtLimitPct(saved.lowMarginPct)} – gilt für alle`,
            { variant: 'success' },
          );
          onClose();
        },
      },
    );
  };

  const onEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') submit();
  };

  const shownError = error ?? (save.isError ? 'Die Grenzen konnten nicht gespeichert werden.' : null);

  return (
    <DsModal
      open
      onClose={onClose}
      title="Grenzen der Farblegende"
      description="Gelten für alle Nutzer: Zeilenfarben, Zähler und Filter der Legende, Margen-Farben der Verkaufspreise und die Auffälligkeiten."
      footer={
        canEdit ? (
          <>
            <DsButton variant="ghost" onClick={onClose} disabled={save.isPending}>
              Abbrechen
            </DsButton>
            <DsButton variant="primary" onClick={submit} disabled={save.isPending}>
              {save.isPending ? 'Speichern…' : 'Speichern'}
            </DsButton>
          </>
        ) : (
          <DsButton onClick={onClose}>Schließen</DsButton>
        )
      }
    >
      <div className="pt-form">
        {shownError && <DsAlert severity="error">{shownError}</DsAlert>}

        <div className="pt-limit-row">
          <span className="pt-limit-swatch pt-state-deal" aria-hidden />
          <label className="pt-limit-line" htmlFor="pt-limit-deal">
            Deal ab
          </label>
          <DsInput
            id="pt-limit-deal"
            className="pt-limit-input"
            mono
            value={deal}
            onChange={(e) => setDeal(e.target.value)}
            onKeyDown={onEnter}
            readOnly={!canEdit}
            inputMode="decimal"
            autoFocus={canEdit}
            aria-describedby="pt-limit-deal-hint"
          />
          <span className="pt-limit-line">% unter MEK</span>
        </div>
        <div id="pt-limit-deal-hint" className="pt-limit-hint">
          Das beste gültige Lieferantenangebot liegt mindestens so weit unter dem MEK. 1 bis 90 %.
        </div>

        <div className="pt-limit-row">
          <span className="pt-limit-swatch pt-state-low-margin" aria-hidden />
          <label className="pt-limit-line" htmlFor="pt-limit-low-margin">
            Niedrige Marge unter
          </label>
          <DsInput
            id="pt-limit-low-margin"
            className="pt-limit-input"
            mono
            value={lowMargin}
            onChange={(e) => setLowMargin(e.target.value)}
            onKeyDown={onEnter}
            readOnly={!canEdit}
            inputMode="decimal"
            aria-describedby="pt-limit-low-margin-hint"
          />
          <span className="pt-limit-line">% Aufschlag</span>
        </div>
        <div id="pt-limit-low-margin-hint" className="pt-limit-hint">
          Mindestens ein Verkaufspreis hat einen Aufschlag von 0 bis unter diesem Wert auf den MEK. 0,1 bis 50 %.
        </div>

        <div className="pt-limit-row">
          <span className="pt-limit-swatch pt-state-under-mek" aria-hidden />
          <span className="pt-limit-line">Unter MEK: fest, Aufschlag unter 0 %</span>
        </div>

        <div className="pt-limit-meta">
          <span>
            {thresholds?.updatedAt
              ? `Zuletzt geändert am ${fmtDateTime(thresholds.updatedAt)}${thresholds.updatedByEmail ? ` von ${thresholds.updatedByEmail}` : ''}`
              : `Standardwerte (Deal ab ${fmtLimitPct(DEFAULT_LEGEND_LIMITS.dealMinSavingsPct)}, niedrige Marge unter ${fmtLimitPct(DEFAULT_LEGEND_LIMITS.lowMarginPct)})`}
          </span>
          {!canEdit && <span>Ändern nur mit dem Recht „Preis-Tool: Einkaufsdaten pflegen“.</span>}
        </div>
      </div>
    </DsModal>
  );
}
